import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type KeyboardEvent } from "react";
import {
  AlertTriangle,
  Archive,
  Check,
  Clipboard,
  Coffee,
  Download,
  FolderOpen,
  ImagePlus,
  Info,
  Lock,
  Monitor,
  Moon,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Sun,
  Upload,
  X
} from "lucide-react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import type { ImageItem, OutputFormat, ProcessingOptions, ResultFilter, ResultSort, WorkspaceTab } from "./types";
import { formatBytes, formatLabels, usesQuality } from "./lib/format";
import { isSupportedImage, normalizeImageForBrowser, readImageDimensions } from "./lib/files";
import { defaultOptions, presets } from "./lib/presets";
import { ImageProcessingClient } from "./lib/workerClient";

const tabs: Array<{ id: WorkspaceTab; label: string; summary: string }> = [
  { id: "compress", label: "Compress", summary: "Reduce size fast" },
  { id: "convert", label: "Convert", summary: "Change format" },
  { id: "resize", label: "Resize", summary: "Set dimensions" },
  { id: "batch", label: "Batch", summary: "Manage many files" },
  { id: "privacy", label: "Privacy", summary: "Local-only export" },
  { id: "advanced", label: "Advanced", summary: "Expert controls" }
];

const formatCards: Array<{ format: OutputFormat; title: string; description: string; warning?: string }> = [
  { format: "webp", title: "WebP", description: "Best everyday choice for small web images." },
  { format: "jpeg", title: "JPEG", description: "Great for photos and broad compatibility.", warning: "Transparency becomes a matte background." },
  { format: "png", title: "PNG", description: "Lossless format that preserves transparency." },
  { format: "avif", title: "AVIF", description: "Very small when your browser supports export.", warning: "Browser support varies." },
  { format: "bmp", title: "BMP", description: "Simple lossless export, usually large.", warning: "Not a compression format." },
  { format: "tiff", title: "TIFF", description: "Baseline lossless export for archival workflows.", warning: "Preview support varies by browser." }
];

const resizePresets = [
  { label: "1200px web image", width: 1200 },
  { label: "1920px large screen", width: 1920 },
  { label: "1080px social", width: 1080 },
  { label: "800px compact", width: 800 }
];

type ThemePreference = "system" | "light" | "dark";
type ResolvedTheme = "light" | "dark";
type FlowStep = "upload" | "settings" | "process" | "results";

const flowSteps: Array<{ id: FlowStep; label: string; summary: string }> = [
  { id: "upload", label: "Upload", summary: "Add files" },
  { id: "settings", label: "Settings", summary: "Pick a task" },
  { id: "process", label: "Process", summary: "Run locally" },
  { id: "results", label: "Results", summary: "Download" }
];

function loadOptions() {
  try {
    const raw = localStorage.getItem("sherlock-image-options");
    if (!raw) return defaultOptions;
    return { ...defaultOptions, ...JSON.parse(raw), resize: { ...defaultOptions.resize, ...JSON.parse(raw).resize } };
  } catch {
    return defaultOptions;
  }
}

function loadTab(): WorkspaceTab {
  const saved = localStorage.getItem("sherlock-image-tab") as WorkspaceTab | null;
  return saved && tabs.some((tab) => tab.id === saved) ? saved : "compress";
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === "system" ? getSystemTheme() : preference;
}

function loadThemePreference(): ThemePreference {
  const saved = localStorage.getItem("sherlock-image-theme");
  return saved === "dark" || saved === "light" || saved === "system" ? saved : "system";
}

export function App() {
  const [items, setItems] = useState<ImageItem[]>([]);
  const [options, setOptions] = useState<ProcessingOptions>(loadOptions);
  const [activeTab, setActiveTab] = useState<WorkspaceTab>(loadTab);
  const [resultFilter, setResultFilter] = useState<ResultFilter>("all");
  const [resultSort, setResultSort] = useState<ResultSort>("newest");
  const [activeStep, setActiveStep] = useState<FlowStep>("upload");
  const [themePreference, setThemePreference] = useState<ThemePreference>(loadThemePreference);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(loadThemePreference()));
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isZipping, setIsZipping] = useState(false);
  const [status, setStatus] = useState("Ready");
  const [clipboardError, setClipboardError] = useState<string | null>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const clientRef = useRef<ImageProcessingClient | null>(null);
  const itemsRef = useRef<ImageItem[]>([]);

  useEffect(() => {
    clientRef.current = new ImageProcessingClient();
    return () => {
      clientRef.current?.dispose();
      itemsRef.current.forEach((item) => {
        URL.revokeObjectURL(item.previewUrl);
        if (item.result?.url) URL.revokeObjectURL(item.result.url);
      });
    };
  }, []);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    localStorage.setItem("sherlock-image-options", JSON.stringify(options));
  }, [options]);

  useEffect(() => {
    localStorage.setItem("sherlock-image-tab", activeTab);
  }, [activeTab]);

  useEffect(() => {
    const applyTheme = () => {
      const nextTheme = resolveTheme(themePreference);
      setResolvedTheme(nextTheme);
      document.documentElement.dataset.theme = nextTheme;
      document.documentElement.style.colorScheme = nextTheme;
      localStorage.setItem("sherlock-image-theme", themePreference);
    };

    applyTheme();
    if (themePreference !== "system" || !window.matchMedia) return;

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    media.addEventListener("change", applyTheme);
    return () => media.removeEventListener("change", applyTheme);
  }, [themePreference]);

  useEffect(() => {
    folderInputRef.current?.setAttribute("webkitdirectory", "");
  }, []);

  const completed = items.filter((item) => item.result);
  const failed = items.filter((item) => item.status === "error");
  const qualityEnabled = usesQuality(options.format);
  const themeLabel = themePreference === "system" ? `System (${resolvedTheme === "dark" ? "Dark" : "Light"})` : themePreference === "dark" ? "Dark" : "Light";
  const cycleThemePreference = () => setThemePreference((current) => (current === "system" ? "dark" : current === "dark" ? "light" : "system"));

  const batchStats = useMemo(() => {
    const totalInput = items.reduce((sum, item) => sum + item.size, 0);
    const totalOutput = completed.reduce((sum, item) => sum + (item.result?.size ?? 0), 0);
    const savings = totalInput && totalOutput < totalInput ? Math.round(((totalInput - totalOutput) / totalInput) * 100) : 0;
    return { totalInput, totalOutput, savings, completed: completed.length, failed: failed.length, total: items.length };
  }, [completed, failed.length, items]);

  const shownItems = useMemo(() => {
    const filtered = items.filter((item) => {
      if (resultFilter === "done") return item.status === "done";
      if (resultFilter === "failed") return item.status === "error";
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (resultSort === "biggest") return b.size - a.size;
      if (resultSort === "savings") return (b.result?.savingsPercent ?? -1) - (a.result?.savingsPercent ?? -1);
      return 0;
    });
  }, [items, resultFilter, resultSort]);

  const updateOptions = (next: Partial<ProcessingOptions>) => setOptions((current) => ({ ...current, ...next }));
  const updateResize = (next: Partial<ProcessingOptions["resize"]>) => setOptions((current) => ({ ...current, resize: { ...current.resize, ...next } }));

  const addFiles = useCallback(async (fileList: FileList | File[]) => {
    const incoming = Array.from(fileList);
    if (!incoming.length) return;

    setStatus("Reading image details");
    const mapped = await Promise.all(
      incoming.map(async (file): Promise<ImageItem> => {
        const base = {
          id: crypto.randomUUID(),
          file,
          processingFile: file,
          name: file.name || "clipboard-image",
          size: file.size,
          type: file.type || "Unknown",
          previewUrl: URL.createObjectURL(file),
          status: "ready" as const,
          progress: 0
        };

        if (!isSupportedImage(file)) {
          return { ...base, status: "error", error: "This file is not a supported image." };
        }

        try {
          const processingFile = await normalizeImageForBrowser(file);
          const dimensions = await readImageDimensions(processingFile);
          const previewUrl = URL.createObjectURL(processingFile);
          URL.revokeObjectURL(base.previewUrl);
          return { ...base, processingFile, previewUrl, ...dimensions };
        } catch (error) {
          return {
            ...base,
            status: "error",
            error: error instanceof Error ? error.message : "This image could not be opened by the browser."
          };
        }
      })
    );

    setItems((current) => [...current, ...mapped]);
    setStatus(`${mapped.length} image${mapped.length === 1 ? "" : "s"} added`);
    setActiveStep("settings");
  }, []);

  const onDrop = useCallback(
    (event: DragEvent<HTMLLabelElement>) => {
      event.preventDefault();
      setIsDragging(false);
      void addFiles(event.dataTransfer.files);
    },
    [addFiles]
  );

  const processAll = useCallback(async () => {
    if (!clientRef.current || isProcessing) return;
    const readyItems = items.filter((item) => item.status !== "error");
    if (!readyItems.length) {
      setStatus("Add at least one supported image first");
      return;
    }

    setIsProcessing(true);
    setStatus("Processing images locally");

    for (const item of readyItems) {
      if (item.result?.url) URL.revokeObjectURL(item.result.url);
      setItems((current) => current.map((entry) => (entry.id === item.id ? { ...entry, status: "processing", progress: 4, error: undefined, result: undefined } : entry)));

      try {
        const result = await clientRef.current.process(item.processingFile, item.name, item.size, options, (progress) => {
          setItems((current) => current.map((entry) => (entry.id === item.id ? { ...entry, progress } : entry)));
        });
        setItems((current) => current.map((entry) => (entry.id === item.id ? { ...entry, status: "done", progress: 100, result } : entry)));
      } catch (error) {
        setItems((current) =>
          current.map((entry) => (entry.id === item.id ? { ...entry, status: "error", progress: 0, error: error instanceof Error ? error.message : "Processing failed." } : entry))
        );
      }
    }

    setStatus("Processing complete");
    setIsProcessing(false);
    setActiveStep("results");
  }, [isProcessing, items, options]);

  const removeItem = (id: string) => {
    setItems((current) => {
      const item = current.find((entry) => entry.id === id);
      if (item) {
        URL.revokeObjectURL(item.previewUrl);
        if (item.result?.url) URL.revokeObjectURL(item.result.url);
      }
      return current.filter((entry) => entry.id !== id);
    });
  };

  const clearAll = () => {
    items.forEach((item) => {
      URL.revokeObjectURL(item.previewUrl);
      if (item.result?.url) URL.revokeObjectURL(item.result.url);
    });
    setItems([]);
    setStatus("Ready");
    setActiveStep("upload");
  };

  const downloadZip = async () => {
    setIsZipping(true);
    try {
      const zip = new JSZip();
      completed.forEach((item) => {
        if (item.result) zip.file(item.result.filename, item.result.blob);
      });
      saveAs(await zip.generateAsync({ type: "blob" }), "sherlock-images.zip");
    } finally {
      setIsZipping(false);
    }
  };

  const pasteFromClipboard = async () => {
    setClipboardError(null);
    try {
      if (!navigator.clipboard?.read) {
        setClipboardError("Clipboard image paste is not available in this browser.");
        return;
      }

      const clipboardItems = await navigator.clipboard.read();
      const files: File[] = [];
      for (const clipboardItem of clipboardItems) {
        const type = clipboardItem.types.find((candidate) => candidate.startsWith("image/"));
        if (!type) continue;
        const blob = await clipboardItem.getType(type);
        files.push(new File([blob], `clipboard-${Date.now()}.${type.split("/")[1]}`, { type }));
      }
      if (!files.length) {
        setClipboardError("No image was found on the clipboard.");
        return;
      }
      await addFiles(files);
    } catch {
      setClipboardError("Clipboard image access is not available in this browser or page context.");
    }
  };

  const setTabFromKeyboard = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    const nextIndex = event.key === "ArrowRight" ? (index + 1) % tabs.length : (index - 1 + tabs.length) % tabs.length;
    setActiveTab(tabs[nextIndex].id);
  };

  const applyPreset = (id: string) => {
    const preset = presets.find((candidate) => candidate.id === id);
    if (preset) setOptions((current) => preset.apply(current));
  };

  const renderTabPanel = () => {
    if (activeTab === "compress") {
      return (
        <TabPanel title="Compress images" eyebrow="Smaller files, fast" helper="Use WebP for a strong default. The app can retry lower quality if the first result is larger than your original.">
          <QuickActions ids={["smaller", "web", "quality"]} applyPreset={applyPreset} />
          <QualityControl enabled={qualityEnabled} options={options} updateOptions={updateOptions} />
          <Switch id="avoid-larger" checked={options.preventLargerOutput} label="Try to avoid larger files" onChange={(value) => updateOptions({ preventLargerOutput: value })} />
          <Switch id="auto-downscale" checked={options.autoDownscaleLargeOutput} label="Allow auto downscale if needed" onChange={(value) => updateOptions({ autoDownscaleLargeOutput: value })} />
          <Helper tone="info">If the result would grow, Sherlock tries smaller lossy exports first. Downscale only runs when you allow it.</Helper>
        </TabPanel>
      );
    }

    if (activeTab === "convert") {
      return (
        <TabPanel title="Convert format" eyebrow="Choose the output type" helper="Pick the format you need. WebP and JPEG are the most common targets for web sharing.">
          <div className="format-grid">
            {formatCards.map((card) => (
              <button key={card.format} className={`format-card ${options.format === card.format ? "selected" : ""}`} type="button" onClick={() => updateOptions({ format: card.format })}>
                <strong>{card.title}</strong>
                <span>{card.description}</span>
                {card.warning && <em>{card.warning}</em>}
              </button>
            ))}
          </div>
          {!["png", "webp", "avif"].includes(options.format) && <MatteControl options={options} updateOptions={updateOptions} />}
        </TabPanel>
      );
    }

    if (activeTab === "resize") {
      return (
        <TabPanel title="Resize dimensions" eyebrow="Fit images to a target" helper="Set a width or height. Leave one blank to keep the original shape.">
          <div className="preset-row">
            {resizePresets.map((preset) => (
              <button key={preset.label} type="button" onClick={() => updateResize({ enabled: true, width: preset.width, height: undefined, maintainAspectRatio: true, onlyShrink: true })}>
                {preset.label}
              </button>
            ))}
          </div>
          <ResizeControls options={options} updateResize={updateResize} />
          <Switch id="only-shrink" checked={options.resize.onlyShrink} label="Only shrink, never enlarge" onChange={(value) => updateResize({ onlyShrink: value })} />
        </TabPanel>
      );
    }

    if (activeTab === "batch") {
      return (
        <TabPanel title="Batch workflow" eyebrow="Folders, queues, and ZIPs" helper="Use one settings set for the whole batch, then download individual files or everything as a ZIP.">
          <div className="batch-actions">
            <button type="button" onClick={() => folderInputRef.current?.click()}><FolderOpen size={18} /> Choose folder</button>
            <button type="button" onClick={() => void downloadZip()} disabled={!completed.length || isZipping}><Archive size={18} /> {isZipping ? "Building ZIP..." : "Download ZIP"}</button>
            <button type="button" onClick={clearAll} disabled={!items.length}><RotateCcw size={18} /> Clear all</button>
          </div>
          <StatsGrid stats={batchStats} />
          <div className="filter-row">
            <label>Show <select value={resultFilter} onChange={(event) => setResultFilter(event.target.value as ResultFilter)}><option value="all">All files</option><option value="done">Completed</option><option value="failed">Failed</option></select></label>
            <label>Sort <select value={resultSort} onChange={(event) => setResultSort(event.target.value as ResultSort)}><option value="newest">Newest</option><option value="biggest">Biggest files</option><option value="savings">Best savings</option></select></label>
          </div>
        </TabPanel>
      );
    }

    if (activeTab === "privacy") {
      return (
        <TabPanel title="Privacy-first export" eyebrow="No uploads, no tracking" helper="Your files are decoded and re-encoded locally. Most metadata is removed during export.">
          <QuickActions ids={["privacy", "safe", "web"]} applyPreset={applyPreset} />
          <div className="privacy-list">
            {["Files stay on your device", "No server-side image processing", "No accounts, ads, analytics, or storage", "EXIF, GPS, and camera details are usually stripped"].map((text) => (
              <span key={text}><ShieldCheck size={18} /> {text}</span>
            ))}
          </div>
          <Helper tone="warning">Color profiles and browser-specific canvas behavior can vary. HEIC decoding also depends on browser support and the file variant.</Helper>
        </TabPanel>
      );
    }

    return (
      <TabPanel title="Advanced controls" eyebrow="Expert settings" helper="Fine tune shared settings. These apply to all files in the queue.">
        <label className="field"><span>Output format</span><select value={options.format} onChange={(event) => updateOptions({ format: event.target.value as OutputFormat })}>{formatCards.map((card) => <option key={card.format} value={card.format}>{card.title}</option>)}</select></label>
        <QualityControl enabled={qualityEnabled} options={options} updateOptions={updateOptions} />
        <ResizeControls options={options} updateResize={updateResize} />
        <Switch id="advanced-avoid" checked={options.preventLargerOutput} label="Retry lower quality when output is larger" onChange={(value) => updateOptions({ preventLargerOutput: value })} />
        <Switch id="advanced-downscale" checked={options.autoDownscaleLargeOutput} label="Allow 75% and 50% auto downscale fallback" onChange={(value) => updateOptions({ autoDownscaleLargeOutput: value })} />
        <MatteControl options={options} updateOptions={updateOptions} />
        <label className="field"><span>Filename suffix</span><input type="text" value={options.suffix} onChange={(event) => updateOptions({ suffix: event.target.value })} /></label>
      </TabPanel>
    );
  };

  return (
    <main>
      <header className="app-header">
          <div className="hero-topline">
            <div className="trust-pill"><Lock size={16} aria-hidden="true" /> Local processing. No upload required.</div>
            <button className="theme-toggle" type="button" aria-label={`Theme preference: ${themeLabel}. Change theme preference.`} onClick={cycleThemePreference}>
              {themePreference === "system" ? <Monitor size={17} aria-hidden="true" /> : resolvedTheme === "dark" ? <Moon size={17} aria-hidden="true" /> : <Sun size={17} aria-hidden="true" />}
              {themeLabel}
            </button>
            <a className="kofi-link" href="https://ko-fi.com/F1F71Z1K12" target="_blank" rel="noreferrer">
              <Coffee size={17} aria-hidden="true" />
              Support me
            </a>
          </div>
        <div className="app-title">
          <h1>Free image converter and compressor.</h1>
          <p>Follow the steps. Add images, choose what to do, process locally, then download.</p>
        </div>
      </header>

      <FlowBreadcrumb activeStep={activeStep} setActiveStep={setActiveStep} itemsCount={items.length} completedCount={completed.length} />

      {activeStep === "upload" && (
        <section className="step-card upload-step" aria-labelledby="upload-heading">
          <div className="step-copy">
            <span className="eyebrow">Step 1</span>
            <h2 id="upload-heading">Add your images</h2>
            <p>Drop files here, choose images, paste from clipboard, or pick a whole folder. Nothing is uploaded.</p>
            <div className="hero-stats" aria-label="Highlights"><span>Free forever</span><span>No tracking</span><span>Bulk ZIP export</span><span>Metadata stripped</span></div>
          </div>
          <UploadPanel isDragging={isDragging} onDrop={onDrop} setIsDragging={setIsDragging} addFiles={addFiles} folderInputRef={folderInputRef} pasteFromClipboard={pasteFromClipboard} clipboardError={clipboardError} />
          {items.length > 0 && (
            <div className="step-actions">
              <button type="button" onClick={clearAll}><RotateCcw size={18} /> Clear</button>
              <button className="primary" type="button" onClick={() => setActiveStep("settings")}>Choose settings</button>
            </div>
          )}
        </section>
      )}

      {activeStep === "settings" && (
        <section className="step-card" aria-labelledby="settings-heading">
          <div className="step-header">
            <div><span className="eyebrow">Step 2</span><h2 id="settings-heading">Choose what to do</h2><p>{items.length ? `${items.length} file${items.length === 1 ? "" : "s"} ready. Pick one workflow and keep the rest tucked away.` : "Add images first, then choose a workflow."}</p></div>
            <StatsGrid stats={batchStats} compact />
          </div>
          <QuickActions ids={["smaller", "webp", "same", "safe", "privacy"]} applyPreset={applyPreset} />
          <div className="tabs" role="tablist" aria-label="Image tasks">
            {tabs.map((tab, index) => (
              <button key={tab.id} id={`${tab.id}-tab`} role="tab" aria-selected={activeTab === tab.id} aria-controls={`${tab.id}-panel`} className={activeTab === tab.id ? "active" : ""} type="button" onClick={() => setActiveTab(tab.id)} onKeyDown={(event) => setTabFromKeyboard(event, index)}>
                <strong>{tab.label}</strong><span>{tab.summary}</span>
              </button>
            ))}
          </div>
          <div id={`${activeTab}-panel`} role="tabpanel" aria-labelledby={`${activeTab}-tab`} className="tab-surface">
            {renderTabPanel()}
          </div>
          <div className="step-actions">
            <button type="button" onClick={() => setActiveStep("upload")}>Back</button>
            <button className="primary" type="button" onClick={() => setActiveStep("process")} disabled={!items.length}>Review queue</button>
          </div>
        </section>
      )}

      {activeStep === "process" && (
        <section className="step-card" aria-labelledby="process-heading">
          <div className="step-header">
            <div><span className="eyebrow">Step 3</span><h2 id="process-heading">Process locally</h2><p>Review the queue, then run the conversion in your browser.</p></div>
            <StatsGrid stats={batchStats} compact />
          </div>
          <div className="queue-actions">
            <button className="primary" type="button" onClick={() => void processAll()} disabled={isProcessing || !items.length}><Upload size={18} /> {isProcessing ? "Processing..." : `Process ${items.length || ""} image${items.length === 1 ? "" : "s"}`}</button>
            <button type="button" onClick={() => folderInputRef.current?.click()}><FolderOpen size={18} /> Add folder</button>
            <button type="button" onClick={clearAll} disabled={!items.length}><RotateCcw size={18} /> Clear all</button>
          </div>
          <p className="status" role="status" aria-live="polite">{status}{items.length > 0 ? ` · ${items.filter((item) => item.status !== "error").length} queued` : ""}{completed.length > 0 ? ` · ${batchStats.savings}% saved` : ""}</p>
          <ResultsPanel items={shownItems} removeItem={removeItem} resultFilter={resultFilter} setResultFilter={setResultFilter} resultSort={resultSort} setResultSort={setResultSort} compact />
          <div className="step-actions">
            <button type="button" onClick={() => setActiveStep("settings")}>Back</button>
            <button className="primary" type="button" onClick={() => setActiveStep("results")} disabled={!completed.length}>See results</button>
          </div>
        </section>
      )}

      {activeStep === "results" && (
        <section className="step-card" aria-labelledby="results-heading">
          <div className="step-header">
            <div><span className="eyebrow">Step 4</span><h2 id="results-heading">Download results</h2><p>Check savings, download one file, or collect everything as a ZIP.</p></div>
            <StatsGrid stats={batchStats} compact />
          </div>
          <div className="queue-actions">
            <button type="button" onClick={downloadZip} disabled={!completed.length || isZipping}><Archive size={18} /> {isZipping ? "Building ZIP..." : "Download ZIP"}</button>
            <button type="button" onClick={() => setActiveStep("upload")}><ImagePlus size={18} /> Add more</button>
          </div>
          <ResultsPanel items={shownItems} removeItem={removeItem} resultFilter={resultFilter} setResultFilter={setResultFilter} resultSort={resultSort} setResultSort={setResultSort} />
          <div className="step-actions">
            <button type="button" onClick={() => setActiveStep("process")}>Back</button>
            <button type="button" onClick={clearAll} disabled={!items.length}><RotateCcw size={18} /> Start over</button>
          </div>
        </section>
      )}
    </main>
  );
}

function UploadPanel({ isDragging, onDrop, setIsDragging, addFiles, folderInputRef, pasteFromClipboard, clipboardError }: { isDragging: boolean; onDrop: (event: DragEvent<HTMLLabelElement>) => void; setIsDragging: (value: boolean) => void; addFiles: (files: FileList | File[]) => Promise<void>; folderInputRef: React.RefObject<HTMLInputElement | null>; pasteFromClipboard: () => Promise<void>; clipboardError: string | null }) {
  return (
    <div className="upload-panel">
      <label className={`dropzone ${isDragging ? "dragging" : ""}`} onDragOver={(event) => { event.preventDefault(); setIsDragging(true); }} onDragLeave={() => setIsDragging(false)} onDrop={onDrop}>
        <input type="file" accept="image/*,.heic,.heif" multiple onChange={(event) => { if (event.target.files) void addFiles(event.target.files); event.currentTarget.value = ""; }} />
        <span className="upload-art" aria-hidden="true"><span className="upload-card one" /><span className="upload-card two" /><ImagePlus size={38} /></span>
        <span className="upload-title">Drop images here</span>
        <small>or choose files. Your images never leave this device.</small>
      </label>
      <input ref={folderInputRef} className="folder-input" type="file" accept="image/*,.heic,.heif" multiple aria-label="Choose a folder of images" onChange={(event) => { if (event.target.files) void addFiles(event.target.files); event.currentTarget.value = ""; }} />
      <div className="upload-buttons">
        <button type="button" onClick={() => folderInputRef.current?.click()}><FolderOpen size={18} /> Choose folder</button>
        <button type="button" onClick={() => void pasteFromClipboard()}><Clipboard size={18} /> Paste</button>
      </div>
      {clipboardError && <p className="error-text" role="alert">{clipboardError}</p>}
    </div>
  );
}

function QuickActions({ ids, applyPreset }: { ids: string[]; applyPreset: (id: string) => void }) {
  return (
    <div className="quick-actions" aria-label="Quick presets">
      {ids.map((id) => {
        const preset = presets.find((candidate) => candidate.id === id);
        if (!preset) return null;
        return <button key={id} type="button" onClick={() => applyPreset(id)}><Sparkles size={15} /> {preset.label}</button>;
      })}
    </div>
  );
}

function FlowBreadcrumb({ activeStep, setActiveStep, itemsCount, completedCount }: { activeStep: FlowStep; setActiveStep: (step: FlowStep) => void; itemsCount: number; completedCount: number }) {
  const activeIndex = flowSteps.findIndex((step) => step.id === activeStep);
  return (
    <nav className="flow-breadcrumb" aria-label="Image tool steps">
      {flowSteps.map((step, index) => {
        const isLocked = (step.id === "settings" || step.id === "process") && itemsCount === 0;
        const isResultsLocked = step.id === "results" && completedCount === 0;
        return (
          <button key={step.id} type="button" className={activeStep === step.id ? "active" : index < activeIndex ? "complete" : ""} disabled={isLocked || isResultsLocked} aria-current={activeStep === step.id ? "step" : undefined} onClick={() => setActiveStep(step.id)}>
            <span>{index + 1}</span>
            <strong>{step.label}</strong>
            <small>{step.summary}</small>
          </button>
        );
      })}
    </nav>
  );
}

function TabPanel({ title, eyebrow, helper, children }: { title: string; eyebrow: string; helper: string; children: React.ReactNode }) {
  return <div className="tab-panel"><span className="eyebrow">{eyebrow}</span><h2>{title}</h2><p>{helper}</p>{children}</div>;
}

function QualityControl({ enabled, options, updateOptions }: { enabled: boolean; options: ProcessingOptions; updateOptions: (next: Partial<ProcessingOptions>) => void }) {
  return <label className="field"><span>Quality {enabled ? `${options.quality}%` : `(${formatLabels[options.format]} is lossless)`}</span><input type="range" min="1" max="100" value={options.quality} disabled={!enabled} onChange={(event) => updateOptions({ quality: Number(event.target.value) })} /></label>;
}

function ResizeControls({ options, updateResize }: { options: ProcessingOptions; updateResize: (next: Partial<ProcessingOptions["resize"]>) => void }) {
  return (
    <div className="resize-grid">
      <Switch id="resize-enabled" checked={options.resize.enabled} label="Resize images" onChange={(value) => updateResize({ enabled: value })} />
      <Switch id="aspect-ratio" checked={options.resize.maintainAspectRatio} label="Maintain aspect ratio" onChange={(value) => updateResize({ maintainAspectRatio: value })} />
      <label className="field"><span>Width</span><input type="number" min="1" placeholder="Auto" value={options.resize.width ?? ""} onChange={(event) => updateResize({ enabled: true, width: event.target.value ? Number(event.target.value) : undefined })} /></label>
      <label className="field"><span>Height</span><input type="number" min="1" placeholder="Auto" value={options.resize.height ?? ""} onChange={(event) => updateResize({ enabled: true, height: event.target.value ? Number(event.target.value) : undefined })} /></label>
    </div>
  );
}

function MatteControl({ options, updateOptions }: { options: ProcessingOptions; updateOptions: (next: Partial<ProcessingOptions>) => void }) {
  return <label className="field color-field"><span>Matte background</span><input type="color" value={options.jpegBackground} onChange={(event) => updateOptions({ jpegBackground: event.target.value })} /><small>Used when exporting formats that do not preserve transparency.</small></label>;
}

function Switch({ id, checked, label, onChange }: { id: string; checked: boolean; label: string; onChange: (checked: boolean) => void }) {
  return <div className="switch-row"><input id={id} type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} /><label htmlFor={id}>{label}</label></div>;
}

function Helper({ children, tone }: { children: React.ReactNode; tone: "info" | "warning" }) {
  return <div className={`helper ${tone}`}><Info size={16} /> {children}</div>;
}

function StatsGrid({ stats, compact = false }: { stats: { totalInput: number; totalOutput: number; savings: number; completed: number; failed: number; total: number }; compact?: boolean }) {
  return <div className={`stats-grid ${compact ? "compact" : ""}`}><span><strong>{stats.total}</strong> files</span><span><strong>{formatBytes(stats.totalInput)}</strong> input</span><span><strong>{stats.completed}</strong> done</span><span><strong>{stats.failed}</strong> failed</span>{!compact && <span><strong>{stats.totalOutput ? formatBytes(stats.totalOutput) : "-"}</strong> output</span>}{!compact && <span><strong>{stats.savings}%</strong> saved</span>}</div>;
}

function ResultsPanel({ items, removeItem, resultFilter, setResultFilter, resultSort, setResultSort, compact = false }: { items: ImageItem[]; removeItem: (id: string) => void; resultFilter: ResultFilter; setResultFilter: (filter: ResultFilter) => void; resultSort: ResultSort; setResultSort: (sort: ResultSort) => void; compact?: boolean }) {
  return (
    <section className={`results ${compact ? "compact-results" : ""}`} aria-label="Image results">
      <div className="results-header">
        <div><h2>Results</h2><p>Scan files, warnings, savings, and downloads.</p></div>
        <div className="filter-row"><label>Show <select value={resultFilter} onChange={(event) => setResultFilter(event.target.value as ResultFilter)}><option value="all">All</option><option value="done">Done</option><option value="failed">Failed</option></select></label><label>Sort <select value={resultSort} onChange={(event) => setResultSort(event.target.value as ResultSort)}><option value="newest">Newest</option><option value="biggest">Biggest</option><option value="savings">Best savings</option></select></label></div>
      </div>
      {items.length === 0 ? <div className="empty-state"><ImagePlus size={48} /><h2>Your images will appear here</h2><p>Upload files, choose a task, and process them locally.</p></div> : <div className="result-grid">{items.map((item) => <ResultCard key={item.id} item={item} removeItem={removeItem} />)}</div>}
    </section>
  );
}

function ResultCard({ item, removeItem }: { item: ImageItem; removeItem: (id: string) => void }) {
  return (
    <article className={`result-card ${item.status}`}>
      <div className="card-header"><div><h3>{item.name}</h3><p>{formatBytes(item.size)} · {item.width && item.height ? `${item.width} x ${item.height}` : "Dimensions unavailable"} · {item.type || "Unknown type"}</p></div><button className="icon-button" type="button" aria-label={`Remove ${item.name}`} onClick={() => removeItem(item.id)}><X size={18} /></button></div>
      <div className="preview-pair"><figure><img src={item.previewUrl} alt={`Original preview for ${item.name}`} /><figcaption>Original</figcaption></figure><figure>{item.result && item.result.format !== "tiff" ? <img src={item.result.url} alt={`Processed preview for ${item.name}`} /> : item.result ? <div className="format-placeholder">{formatLabels[item.result.format]} ready</div> : <div className="preview-placeholder" />}<figcaption>Processed</figcaption></figure></div>
      {item.status === "processing" && <div className="progress" aria-label={`${item.name} processing progress`}><span style={{ width: `${item.progress}%` }} /></div>}
      {item.error && <p className="error-text" role="alert">{item.error}</p>}
      {item.result && <div className="metrics"><span><Check size={16} /> {formatLabels[item.result.format]}</span><span>{formatBytes(item.result.size)}</span><span>{item.result.width} x {item.result.height}</span><strong>{item.result.wasLargerThanOriginal ? "Larger output" : `${item.result.savingsPercent}% saved`}</strong>{item.result.optimizationApplied && <span className="optimization-note">{item.result.optimizationApplied}</span>}{item.result.wasLargerThanOriginal && <div className="size-warning"><AlertTriangle size={16} /> Output is larger than the original. Try WebP/JPEG, lower quality, or enable auto downscale.</div>}<a className="download-link" href={item.result.url} download={item.result.filename}><Download size={16} /> Download</a></div>}
    </article>
  );
}
